import { useEffect } from "react";

export const NEXUS_BOOT_RELOAD_MS = 30_000;
export const NEXUS_BOOT_RELOAD_MAX = 5;
export const NEXUS_BOOT_RELOAD_COUNT_KEY = "bf-nexus-boot-reloads";

function readCount(): number {
  try {
    const count = parseInt(sessionStorage.getItem(NEXUS_BOOT_RELOAD_COUNT_KEY) ?? "", 10);
    return Number.isNaN(count) ? 0 : count;
  } catch (e) {
    return 0;
  }
}

function writeCount(count: number): void {
  try {
    sessionStorage.setItem(NEXUS_BOOT_RELOAD_COUNT_KEY, String(count));
  } catch (e) {
    // storage unavailable: the cap cannot persist, reloads keep happening
  }
}

function clearCount(): void {
  try {
    sessionStorage.removeItem(NEXUS_BOOT_RELOAD_COUNT_KEY);
  } catch (e) {
    // storage unavailable: nothing to clear
  }
}

/**
 * Reloads the page every `NEXUS_BOOT_RELOAD_MS` while the first Nexus load has not finished.
 *
 * The Nexus client has no request timeout by design, so a connection that hangs would leave the app on its
 * boot loader indefinitely. `ready` only ever goes false -> true. Consecutive reloads are counted in
 * sessionStorage and capped at `NEXUS_BOOT_RELOAD_MAX`; the counter is cleared once `ready` is true so a later
 * hang in the same tab gets a fresh budget.
 */
export function useReloadUntilNexusReady(ready: boolean): void {
  useEffect(() => {
    if (ready) {
      clearCount();
      return undefined;
    }

    const count = readCount();
    if (count >= NEXUS_BOOT_RELOAD_MAX) {
      return undefined;
    }

    const timer = setTimeout(() => {
      writeCount(count + 1);
      window.location.reload();
    }, NEXUS_BOOT_RELOAD_MS);

    return () => clearTimeout(timer);
  }, [ready]);
}
